import type { DrawingEntity, Point } from '../contract/types.js';

/**
 * The matching tolerance.
 *
 * This is specified by the board, not proposed by us:
 *
 *     T = max(0.2 mm, drawing diagonal x 0.001)
 *
 * An absolute floor with a size-proportional term, so a large drawing is not
 * held to a tighter relative standard than a small one.
 *
 * Note that T is defined in **drawing millimetres**. A drawing whose physical
 * scale could not be determined therefore has no tolerance to be measured
 * against at all — which is why an undetermined scale makes a result partial
 * rather than merely imprecise, and why matching operates on geometry that has
 * been moved into drawing space.
 */

export const TOLERANCE_FLOOR_MM = 0.2;
export const TOLERANCE_DIAGONAL_FRACTION = 0.001;

/** Arcs carry an additional angular tolerance, also fixed by the board. */
export const ARC_ANGLE_TOLERANCE_DEG = 0.5;

export function matchTolerance(diagonalMm: number): number {
  return Math.max(TOLERANCE_FLOOR_MM, diagonalMm * TOLERANCE_DIAGONAL_FRACTION);
}

/** The diagonal of the bounding box of everything in the drawing, in millimetres. */
export function drawingDiagonal(entities: readonly DrawingEntity[]): number {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const see = (p: Point): void => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  };

  for (const e of entities) {
    switch (e.type) {
      case 'LINE':
        see(e.start);
        see(e.end);
        break;
      case 'LWPOLYLINE':
        for (const v of e.vertices) see(v);
        break;
      case 'CIRCLE':
      case 'ARC':
        see({ x: e.centre.x - e.radius, y: e.centre.y - e.radius });
        see({ x: e.centre.x + e.radius, y: e.centre.y + e.radius });
        break;
      case 'TEXT':
      case 'MTEXT':
        see(e.anchor);
        break;
    }
  }

  if (!Number.isFinite(minX)) return 0;
  return Math.hypot(maxX - minX, maxY - minY);
}
